import { dateUtils } from '@helpers/dateUtils';
import { shortId } from '@utils/shortid';
import {
  Campaign,
  Category,
  DocumentScope,
  Entity,
  EntitySettings,
  EntityStudioConfig,
  FormGroup,
  FormIntent,
  GroupEntity,
  Reminder,
  ResourceGrant,
  Role,
  SurveyResponse,
  TableItem,
  Tag,
  TenantScope,
  UserGroup
} from '@vincerosoft/assessfluence-types';
import { accountStore } from 'src/stores/bootstrap-stores/account';

export const defaultDataModels = () => {
  const docScope = accountStore.getState().docScope;

  const groupEntity = (ownerId: string): GroupEntity => ({
    id: `ge_${shortId()}`,
    name: '',
    object: 'group-entity',
    created_at: new Date() as unknown as Date,
    primary_owner_id: null as unknown as string,
    activated_modules: [],
    owned_by: ownerId,
    custom_data: {} as GroupEntity['custom_data'],
    options: {},
    status: 'active',
    seats: { status: 'enabled', type: 'capped', limit: 0 } as GroupEntity['seats']
  });
  const tableItem = (tableId: string): TableItem => ({
    id: 'ti_' + shortId(),
    object: 'table-item',
    created_at: new Date() as unknown as Date,
    table_id: tableId,
    external_id: '',
    data: {} as TableItem['data'],
    label: '',
    ...(docScope as DocumentScope),
    ...(docScope.domain !== 'ENTITY' && { shared_entity_ids: [] })
  });
  const entity = (ownerId: string): Entity => {
    const baseEntity = {
      id: 'ent_' + shortId(),
      object: 'entity' as const,
      created_at: new Date() as unknown as Date,
      status: 'active' as const,
      usage: {
        responses: 0,
        seats: 0
      },
      owned_by: ownerId,
      name: '',
      custom_data: {},
      profile_data: {},
      seats: { status: 'enabled', type: 'capped', limit: 0 } as Entity['seats'],
      primary_admin_id: '',
      activated_modules: [],
      options: {},
      ...(docScope as TenantScope)
    };

    return baseEntity;
  };
  const resourceGrant = (entityId: string): ResourceGrant => ({
    id: 'rg_' + shortId(),
    created_at: new Date(),
    object: 'resource-grant',
    entity_id: entityId,
    domain: 'ENTITY',
    resource_id: '',
    status: 'active',
    resource_type: 'form'
  });
  const campaign = (ownerId: string, entityId: string): Campaign => ({
    id: 'cam_' + shortId(),
    object: 'campaign' as const,
    created_at: new Date(),
    status: 'draft' as Campaign['status'],
    owned_by: ownerId,
    user_group_id: null as unknown as string,
    send_timestamp: null as unknown as string,
    scheduled_date: null as unknown as string,
    name: '',
    tags: [],
    is_draft: true,
    entity_id: entityId,
    closing_date: dateUtils.add(3, 'months').toDate() as unknown as Date,
    responses_count: 0,
    resource: { id: '', type: 'form' },
    audience_count: 0,
    is_public: false,
    type: null as unknown as Campaign['type'],
    trigger: 'campaigns',
    step_id: 0,
    prefill_data: {},
    audience: [],
    email_id: '',
    table_item_id: ''
  });
  const category = (): Category => ({
    id: 'cat_' + shortId(),
    object: 'category',
    created_at: new Date(),
    name: '',
    form_ids: [],
    form_group_ids: [],
    ...(docScope as DocumentScope)
  });
  const response = (entityId: string): SurveyResponse => ({
    id: 'res_' + shortId(),
    object: 'response',
    created_at: new Date(),
    lang: 'en',
    invitation: undefined,
    user_group_id: '',
    respondent: undefined,
    campaign: undefined,
    form: {
      id: '',
      name: '',
      intent: null as unknown as FormIntent
    },
    is_anonymous: false,
    table_item_id: '',
    last_page_no: 0,
    partial: true,
    marked_as_settled: false,
    data: null as unknown as SurveyResponse['data'],
    entity_id: entityId
  });
  const tag = (context: Tag['context']): Tag => ({
    id: 'tag_' + shortId(),
    object: 'tag',
    created_at: new Date(),
    name: '',
    ...(context === 'studio'
      ? { context: 'studio', form_ids: [], form_group_ids: [] }
      : context === 'entities'
        ? { context: 'entities', entity_ids: [] }
        : { context: 'campaigns', campaign_ids: [] }),
    ...(docScope as DocumentScope)
  });
  const reminder = (
    campaignId: string
  ): Reminder & {
    created_at: Date;
  } => ({
    id: 're_' + shortId(),
    object: 'reminder',
    created_at: new Date(),
    trigger: 'ad_hoc',
    campaign_id: campaignId,
    status: 'pending',
    error: '',
    member_ids: []
  });
  const formGroup = (): FormGroup =>
    ({
      id: 'gro_' + shortId(),
      object: 'form-group',
      advance_policy: 'immediate',
      created_at: new Date(),
      status: 'active',
      name: null as unknown as string,
      description: null as unknown as string,
      intent: null as unknown as string,
      form_stacks: [],
      options: {},
      ...(docScope as DocumentScope)
    }) as FormGroup;
  const role = (ownerId: string): Role => ({
    id: 'role_' + shortId(),
    created_at: new Date(),
    object: 'role' as const,
    name: '',
    description: '',
    owned_by: ownerId,
    ...(docScope as DocumentScope),
    roles_granted: [],
    user_type: 'user' as Role['user_type'],
    permissions: []
  });
  const userGroup = (ownerId: string): UserGroup =>
    ({
      id: 'grp_' + shortId(),
      object: 'user-group',
      owned_by: ownerId,
      name: '',
      description: '',
      form_ids: [],
      form_group_ids: [],
      data_contributors: [],
      member_ids: [],
      ...(docScope as DocumentScope)
    }) as UserGroup;
  const entityStudioConfig = (resource: { id: string; type: string }) =>
    ({
      id: 'esc_' + shortId(),
      object: 'entity-studio-config',
      created_at: new Date(),
      resource: resource,
      ...(docScope as DocumentScope),
      email_templates: {},
      approval_flow: {} as EntityStudioConfig['approval_flow']
    }) as EntityStudioConfig;
  const entitySettings = () =>
    ({
      id: 'es_' + shortId(),
      created_at: new Date(),
      object: 'entity-settings',
      email_templates: {},
      ...(docScope as DocumentScope)
    }) as EntitySettings;

  return {
    entity,
    groupEntity,
    resourceGrant,
    campaign,
    category,
    response,
    tag,
    reminder,
    formGroup,
    role,
    userGroup,
    entityStudioConfig,
    entitySettings,
    tableItem
  };
};
